import { assetUrl } from "@/utils/assetUrl";

interface PainPoint {
  icon: string;
  title: string;
  desc: string;
}

interface Props {
  painPoints: PainPoint[];
  sceneName: string;
  accentColor: string;
  painPointsImage?: string;
}

/** 根据痛点数量返回合适的 grid 列数 class */
function getGridCols(count: number): string {
  if (count <= 2) return "grid-cols-1 sm:grid-cols-2";
  if (count === 3) return "grid-cols-1 sm:grid-cols-3";
  if (count === 4) return "grid-cols-2 lg:grid-cols-4";
  if (count === 5) return "grid-cols-2 lg:grid-cols-5";
  return "grid-cols-2 lg:grid-cols-3";
}

/** 连接线只在同一行的相邻卡片之间显示 */
function isLastInRow(index: number, count: number, cols: number): boolean {
  return (index + 1) % cols === 0 || index === count - 1;
}

export default function DetailPainPoints({ painPoints, sceneName, accentColor, painPointsImage }: Props) {
  const count = painPoints.length;
  if (count === 0) return null;

  let desktopCols = 4;
  if (count <= 2) desktopCols = count;
  else if (count === 3) desktopCols = 3;
  else if (count === 5) desktopCols = 5;
  else if (count >= 6) desktopCols = 3;

  return (
    <section className="py-16 lg:py-20 bg-[#f8f9fb]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
          <div className="flex items-start gap-3">
            <div
              className="w-1 h-12 rounded-full mt-1 flex-shrink-0"
              style={{ backgroundColor: accentColor }}
            />
            <div>
              <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-1">
                Pain Point Analysis
              </p>
              <h2 className="text-xl lg:text-2xl font-bold text-[#0d1f3c]">
                您是否也面临这些困境？
              </h2>
              <p className="text-sm text-gray-500 mt-1.5 max-w-lg leading-relaxed">
                深入了解企业在{sceneName}领域的核心挑战，找到真正适合您的解决之道
              </p>
            </div>
          </div>
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-semibold flex-shrink-0"
            style={{
              borderColor: `${accentColor}30`,
              backgroundColor: `${accentColor}08`,
              color: accentColor,
            }}
          >
            <i className="ri-focus-3-line text-sm" />
            <span>{count} 大核心痛点</span>
          </div>
        </div>

        {/* 痛点分析配图 */}
        {painPointsImage && (
          <div
            className="mb-10 rounded-2xl overflow-hidden border"
            style={{ borderColor: `${accentColor}18` }}
          >
            <img
              src={assetUrl(painPointsImage)}
              alt={`${sceneName}痛点分析`}
              className="w-full h-auto block"
            />
          </div>
        )}

        {/* Cards grid */}
        <div className={`grid gap-6 ${getGridCols(count)}`}>
          {painPoints.map((point, index) => (
            <div key={index} className="flex flex-col">
              {/* Step number + connector line */}
              <div className="flex items-center mb-5">
                <div
                  className="w-12 h-12 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                  style={{
                    borderColor: accentColor,
                    color: accentColor,
                    backgroundColor: `${accentColor}08`,
                  }}
                >
                  <span className="text-sm font-bold tracking-wider">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                {!isLastInRow(index, count, desktopCols) && (
                  <div
                    className="hidden lg:block flex-1 h-px ml-3"
                    style={{ backgroundColor: `${accentColor}25` }}
                  />
                )}
              </div>

              {/* Card */}
              <div
                className="flex-1 bg-white rounded-xl p-6 border transition-all duration-200 cursor-default"
                style={{ borderColor: `${accentColor}18` }}
              >
                {/* Icon */}
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-lg mb-4"
                  style={{
                    backgroundColor: `${accentColor}10`,
                    border: `1px solid ${accentColor}20`,
                  }}
                >
                  <i
                    className={`${point.icon || "ri-error-warning-line"} text-lg`}
                    style={{ color: accentColor }}
                  />
                </div>

                {/* Title */}
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-2 leading-snug">
                  {point.title}
                </h3>

                {/* Desc */}
                <p className="text-xs text-gray-500 leading-relaxed">
                  {point.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
